"use client"

import { useEffect, useRef, useState } from "react"
import posthog from "posthog-js"
import { isChunkLoadError, recoverFromChunkError } from "@/lib/chunk-error-recovery"

interface PublicErrorBoundaryProps {
  error: Error & { digest?: string }
  reset: () => void
  source: string
  mode?: "auto-reload" | "graceful"
  reloadAfterMs?: number
}

const RETRY_INTERVAL_MS = 5000

export function PublicErrorBoundary({
  error,
  reset,
  source,
  mode = "graceful",
  reloadAfterMs = RETRY_INTERVAL_MS,
}: PublicErrorBoundaryProps) {
  const isChunkError = isChunkLoadError(error)
  const [countdown, setCountdown] = useState(Math.ceil(reloadAfterMs / 1000))
  const reportedRef = useRef(false)

  // Stale or missing chunk after a deploy: hand it to the boot watchdog, which
  // reloads once the chunk is reachable again (lib/chunk-error-recovery.ts).
  useEffect(() => {
    if (!isChunkError) return
    recoverFromChunkError(error, source)
  }, [error, isChunkError, source])

  useEffect(() => {
    if (isChunkError) return // chunk errors are auto-recovery, not exceptions
    if (reportedRef.current) return
    reportedRef.current = true
    console.error(`[${source}]`, error)
    try {
      if (posthog.__loaded) {
        posthog.capture("$exception", {
          $exception_message: error.message,
          $exception_type: "react-error",
          $exception_stack_trace_raw: error.stack,
          $exception_source: source,
          $exception_digest: error.digest,
          $current_url: typeof window !== "undefined" ? window.location.href : "",
          mode,
        })
      }
    } catch {
      // Silent fail
    }
  }, [error, source, mode, isChunkError])

  useEffect(() => {
    if (isChunkError) return
    if (mode !== "auto-reload") return
    if (typeof window === "undefined") return

    const tickInterval = setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : 0))
    }, 1000)

    const reloadTimer = setTimeout(() => {
      try {
        window.location.reload()
      } catch {
        reset()
      }
    }, reloadAfterMs)

    return () => {
      clearInterval(tickInterval)
      clearTimeout(reloadTimer)
    }
  }, [mode, reloadAfterMs, reset, isChunkError])

  // Graceful "Try again". React `reset()` only re-renders the SAME in-memory
  // JS — for a tab stuck on a stale (pre-fix) build that's an infinite loop:
  // it never hits the network, so the fixed build can never reach it. A full
  // reload fetches fresh HTML → fresh build → the user escapes. Guarded
  // one-reload-per-pathname-per-session (mirrors recoverFromChunkError) so a
  // genuinely-broken *fresh* build can't loop — after one reload, fall back
  // to React reset() (in-memory retry; polling hooks recover on next cycle).
  const handleRetry = () => {
    if (typeof window === "undefined") {
      reset()
      return
    }
    const key = `error-reload:${window.location.pathname}`
    try {
      if (!window.sessionStorage.getItem(key)) {
        window.sessionStorage.setItem(key, "1")
        window.location.reload()
        return
      }
    } catch {
      // sessionStorage blocked (private mode / strict cookies) — a plain
      // reload still beats an in-memory retry for a stale-build tab.
      window.location.reload()
      return
    }
    reset()
  }

  // Chunk-load failures recover via the watchdog (see effect above), which may
  // wait for the chunk to come back before reloading. Show that something is
  // happening instead of the blank page this used to render (task-168).
  if (isChunkError) return <ChunkUpdating />

  if (mode === "auto-reload") {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f9fafb",
          padding: 24,
          fontFamily: "system-ui, sans-serif",
        }}
      >
        <div style={{ textAlign: "center", maxWidth: 480 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>—</div>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#111827", margin: "0 0 12px" }}>
            Rates temporarily unavailable
          </h2>
          <p style={{ fontSize: 16, color: "#6b7280", margin: "0 0 24px" }}>
            Reloading in {countdown}s…
          </p>
          <button
            onClick={() => window.location.reload()}
            type="button"
            className="btn-depth"
            style={{
              backgroundColor: "#01323E",
              color: "#fff",
              border: "none",
              borderRadius: 24,
              padding: "10px 20px",
              fontSize: 14,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Reload now
          </button>
        </div>
      </div>
    )
  }

  // Graceful mode: surface a clear placeholder, no auto-reload — let the user
  // decide. Polling hooks recover on next cycle anyway.
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white dark:bg-[#2a2a2a] border border-gray-200 dark:border-gray-700 rounded-3xl shadow-xl p-8 text-center">
        <div className="text-4xl text-gray-400 mb-3 select-none">—</div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          Rates temporarily unavailable
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          We hit a snag rendering this page. The data will refresh shortly.
        </p>
        <button
          onClick={handleRetry}
          type="button"
          className="px-6 py-2.5 rounded-[20px] bg-[#01323E] hover:bg-[#013845] text-white text-sm font-medium transition-colors btn-depth"
        >
          Try again
        </button>
      </div>
    </div>
  )
}

/** Neutral placeholder while a chunk-load failure recovers (reload pending). */
export function ChunkUpdating() {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        fontFamily: "system-ui, sans-serif",
        color: "#6b7280",
        fontSize: 16,
      }}
    >
      {/* Locale-neutral: the translation provider may be the thing that failed. */}
      Updating… / Обновление… / Թարմացում…
    </div>
  )
}
